import { appConfig } from 'appConfig'
import { rgba } from 'polished'

const BaseColors = {
    Black: '#050a10',
    White: '#ffffff',
    WhiteOpacity45: 'rgba(255,255,255, .45)',

    Grey: '#69728B',
    GreyLight: '#abb4c9',
    Grey1: '#f8f8f8',
    Grey2: '#858891',
    Grey3: '#303d58',
    Grey4: '#B1B4BD',
    Grey5: '#677497',
    Grey6: '#707688',
    Grey7: '#34435F',
    Grey8: '#9BA2B0',
    Grey9: rgba(247, 247, 250, 0.5),
    Grey10: '#F7F7FA',
    Pink: '#D316A4',
    Purple: '#8C12FF',
    Blue: '#1263FF',

    Transparent: 'rgba(0,0,0,0)',
    TransparentBlack: 'rgba(0,0,0,0.6)',
    TransparentWhite: 'rgba(255,255,255,0.07)',
    TransparentGrey: '#E5E6EA',

    Green: '#32a538',
    DarkGreen: '#0a6e2c',
    LightGreen: '#45eb4d',
    ToastGreen: '#409244',
    ATCGreen: '#265829',
    PaleGreen: '#a6d46d',
    VeryPaleOrange: '#F6F0E9',
    UltraPaleGreen: '#E9F6EA',
    SoftGreen: '#81c48d',
    Yellow: '#f8ba00',
    DarkYellow: '#664A02',
    DarkYellow2: '#996600',
    PaleOrange: '#f1a267',
    Orange: '#ff6d00',
    Red: '#e42626',
    PaleRed: '#F6E9E9',
    DarkRed: '#280006',
    Magenta: '#da58ab',
    SoftMagenta: '#d958ab',
    Violet: '#6c55a7',
    SoftBlue: '#4c85ff',
    SeaBlue: '#4dc8c9',
    LightBlue: '#E9EEF6',
    PaleBlue: '#DFE9FF',
    TaskBlue: '#1A62FB',
    IntensiveBlue: '#1D62FB',
    TaskYellow: '#ffd145',
    DarkBlue: '#060B14',
    InfoToastBlue: '#005de5',
    ProgressGreen: '#69F996',
    ProgressRed: '#FF073A',
    ProgressGrey: '#D9D9D9',
    ProgressYellow: '#FFB904',
    ProgressBarGradient: 'linear-gradient(180deg, rgba(108,255,156,1) 0%, rgba(50,165,56,1) 100%)',
    UnitTaskBlack: '#16181C',
    UnitTaskYellow: '#332500',
    UnitTaskGreen: '#163320',
    UnitTaskRed: '#140001',
    UnitTaskGrey: '#B0B8C3',
}

const AosColors = {
    //Primary colors
    Primary: '#005de5',
    PrimaryLight: '#417eff',
    PrimaryDark: '#0f4aca',
    PrimaryPale: '#7ca6ff',

    BackgroundPrimary: '#0b1029',
    BackgroundLight: '#182146',
    BackgroundDark: '#232d43',
    BackgroundSection: '#20293A',
    // Redesign panel chrome (Figma EventBackground) — theme-agnostic dark.
    BackgroundCanvas: '#111114',
    BackgroundSubSurface: '#18181B',
    BackgroundElevation: '#262629',
    BorderSubtle: '#2A2D32',
    BorderStrong: '#505051',
    ContentOnGraphics: '#2B2B2B',
    StatusSuccess: '#54CC49',
    StatusWarning: '#F8D41D',
    StatusCritical: '#E72400',
    StatusCaution: '#FF8B1F',
    StatusNeutral: '#8B9EB4',
    BackgroundSectionLight: '#2D3952',
    RemoveAccountBackground: BaseColors.White,
    RemoveAccountBorder: '#b9b9b9',

    TextPrimary: '#FFFFFF',
    TextSecondary: '#B0B8C3',
    TextDarkPrimary: '#14202C',
    TextDarkSecondary: '#5C6B7A',
    TextTheme: '#FFFFFF',
    TextButtonLabel: '#005de5',

    IconDarkPrimary: '#2D2D2D',
    IconPrimary: '#FFF',

    //App specific colors
    WidgetFontColor: '#FFFFFF',
    FlightsScreenAirportSelectBackground: '#005de5',

    PrimaryButtonHoverBackground: '#417eff',
    PrimaryOutlinedButtonHoverBackground: '#417eff',

    ProgressBarBackground: 'rgba(24, 33, 70, 0.4)',

    ProgressBar: '#FFF',
    MobileReportingItem: '#182146',

    MenuButtonColor: '#6780aa', //UiBlack4
    MessageSpecificBackgroundColor: '#6780aa',
    StatusTileNeutral: '#6780aa',
    FireFighterTileBorder: '#6780aa',
    MobileContactDetailsBorder: '#6780aa',
    MobileDescriptionInputBorder: '#6780aa',
    MobileReportingItemIconWrapperBorder: '#6780aa',
    FormListSeparator: '#6780aa',
    FormSwitchBase: '#6780aa',
    BadgeLabelBorder: '#6780aa',
    UserPhotoBase: '#6780aa',
    ToggleBase: '#6780aa',

    ReadOnlyInputBase: '#6780aa',

    SideMenuInactive: '#6780aa',
    WidgetSecondary: '#ACADAF',
    WidgetPrimary: '#E5E5E6',
    TabBarActive: '#fff',
    TabBarInactive: '#000',
    ChartBase: '#3b4b6a', //UiBlack3
    DashboardItemBackground: '#1B2435',
    DisabledText: '#c6c7cb', //Grey2
    TimelineBackground: '#14202c', // UiBlack1
    FlightGrey: 'rgb(61,75,107)',
    FlightGrey2: 'rgb(104, 128, 170)',
    ActiveGrey: '#2D3952',
    ContactListItem: '#20293A',
    ToastFont: '#232324',
    Tint: '#417eff',
    ChecklistBackground: '#18202a',
    HeaderActiveTabUnderline: BaseColors.White,
    LightScrollbar: 'rgba(193, 193, 193, .8)',
    ContactFormAddNewButtonColor: '#417eff',
    EventDetailsSelectLocationLinkColor: '#69728B',
    OutlineButtonBackground: '#FFF',
    PrimaryComponentLabel: BaseColors.White,
    TransparentOutlinedButtonHoverBackground: BaseColors.White,
    DashboardWarningBadge: BaseColors.Yellow,
    RadioPrimary: '#3b4b6a',

    MobileContactEmailButtonBackground: '#6780aa',
    MobilePrivateChannelInvitationRoleObserveButtonBackground: '#6780aa',

    BottomTabActive: '#FFFFFF',
    BottomTabInactive: '#6780aa',
    DisabledButton: '#677497',
    MapFill: 'rgba(208, 220, 156, 0.5)',
    MapBorder: 'rgba(245, 247, 156, 1)',
    DarkInput: BaseColors.Black,
    WeatherChartBackground: '#1b2437',
    TransparentButtonBorder: BaseColors.White,
    TransparentButtonText: BaseColors.White,
    TransparentButtonTextHover: BaseColors.Black,
    LinkTextDark: '#005de5',
    LinkTextLight: '#7ca6ff',
    ChartAxisLineStroke: '#3b4b6a',
    ChartAxisLabel: '#69728b',
    UnderlineTabsActiveColor: '#005de5',
    PrimaryIconBoxButtonTint: BaseColors.White,
    ThemeOnWhite: '#005de5',
    AddAttachmentButton: '#182146',
    AddAttachmentButtonContent: BaseColors.White,

    CheckboxPrimary: '#005de5',
    CheckboxTickLight: BaseColors.White,
    CheckboxTickDark: BaseColors.White,
    CheckboxInactiveLight: BaseColors.Grey2,
    CheckboxInactiveDark: BaseColors.Grey2,

    MobileFlightInfoNatureOfFlightBase: '#6780aa',

    ChecklistTaskRadio: '#3b4b6a',

    AirportContactButton: '#182146',
    WeatherIconNormal: '#14202c',
    WeatherIconRain: '#639CFF',
    WeatherIconSnow: '#4dc8c9',
    WeatherIconRainSnow: '#6c55a7',
    GraphInactiveColor: '#3C4B69',
    GraphBackground: '#232d43',

    RunwayHelp: '#FFF',
    RunwayHelpFade: '#3C4B69',
    RunwayLegendDeparture: '#005DE5',
    ReportingModalBottomBackground: '#f8ba00',
    RunwayMetar: rgba('#FFFFFF', 0.73),
    Onboarding: '#293158',
    StandsAndGatesItem: '#69728B',

    OnboardingGradient1: '#005de5',
    OnboardingGradient2: '#0f4aca',

    DiscountModuleAreaName: '#abb4c9',
    DiscountItemBackgroundMobile: '#182146',

    RunwayActive: '#FFF',
    RunwayInactive: '#3C4B69',

    NotificationSecondary: '#FFF',
    NotamOutline: '#6780aa',
    AdvancedFilterBorder: '#303d58',
    KanbanColumn: '#1B2535',
    KanbanTransparentColumn: '#1b2535e6',
    KanbanTransparentColumnLight: '#1b253580',
    KanbanTaskCancelledLabel: '#8F8F8F',
    HeaderMoreBadge: '#639CFF',
    HeaderMoreBadgeActive: '#005DE5',
}

const AoimsColors = {
    PrimaryLight: '#FFDD4F',
    Primary: '#FFDD4F',
    PrimaryDark: '#b08f22',
    PrimaryPale: '#2D2D2D',

    BackgroundPrimary: '#0F0F0F',
    BackgroundLight: '#1C1C1C',
    BackgroundDark: '#2D2D2D',
    BackgroundSection: '#1C1C1C',
    // Redesign panel chrome (Figma EventBackground) — theme-agnostic dark.
    BackgroundCanvas: '#111114',
    BackgroundSubSurface: '#18181B',
    BackgroundElevation: '#262629',
    BorderSubtle: '#2A2D32',
    BorderStrong: '#505051',
    ContentOnGraphics: '#2B2B2B',
    StatusSuccess: '#54CC49',
    StatusWarning: '#F8D41D',
    StatusCritical: '#E72400',
    StatusCaution: '#FF8B1F',
    StatusNeutral: '#8B9EB4',
    BackgroundSectionLight: '#2D2D2D',
    RemoveAccountBackground: '#EAEEEF',
    RemoveAccountBorder: '#b9b9b9',

    TextPrimary: '#FFFFFF',
    TextSecondary: '#EAEAEA',
    TextDarkPrimary: '#0F0F0F',
    TextDarkSecondary: '#858891',
    TextTheme: '#0F0F0F',
    TextButtonLabel: '#6A5600',

    WidgetFontColor: '#B9B9B9',
    FlightsScreenAirportSelectBackground: '#2D2D2D',

    PrimaryOutlinedButtonHoverBackground: '#b08f22',
    PrimaryButtonHoverBackground: '#b08f22',

    ProgressBarBackground: '#FFF1B8',
    ProgressBar: '#1C1C1C',

    MobileReportingItem: '#2D2D2D',

    IconDarkPrimary: '#2D2D2D',
    IconPrimary: '#FFF',

    MenuButtonColor: '#FFFFFF',
    MessageSpecificBackgroundColor: '#FFFFFF',
    StatusTileNeutral: '#FFFFFF',
    FireFighterTileBorder: '#FFFFFF',
    MobileContactDetailsBorder: '#FFFFFF',
    MobileDescriptionInputBorder: '#FFFFFF',
    MobileReportingItemIconWrapperBorder: '#FFFFFF',
    ReadOnlyInputBase: '#FFFFFF',
    FormListSeparator: '#FFFFFF',
    FormSwitchBase: '#FFFFFF',
    BadgeLabelBorder: '#FFFFFF',
    UserPhotoBase: '#FFFFFF',

    ToggleBase: '#FFFFFF',

    SideMenuInactive: '#8b8b8b',
    WidgetSecondary: '#ACADAF',
    WidgetPrimary: '#E5E5E6',

    TabBarActive: rgba('#000', 1),
    TabBarInactive: rgba('#000', 0.73),
    ChartBase: '#5a5a5a',

    DashboardItemBackground: '#1C1C1C',
    DisabledText: '#c6c7cb',
    TimelineBackground: '#0F0F0F',
    FlightGrey: '#B9B9B9',
    FlightGrey2: '#B9B9B9',
    ActiveGrey: '#5a5a5a',
    ContactListItem: '#2D2D2D',

    ToastFont: '#232324',
    Tint: '#2D2D2D',
    ChecklistBackground: '#18202a',
    HeaderActiveTabUnderline: '#0F0F0F',
    LightScrollbar: 'rgba(193, 193, 193, .8)',
    ContactFormAddNewButtonColor: '#b08f22',
    EventDetailsSelectLocationLinkColor: '#B9B9B9',
    OutlineButtonBackground: '#FFDD4F',
    PrimaryComponentLabel: '#0F0F0F',
    TransparentOutlinedButtonHoverBackground: '#FFFFFF80',
    DashboardWarningBadge: '#FF903C',
    RadioPrimary: '#FFDD4F',

    MobileContactEmailButtonBackground: '#5a5a5a',
    MobilePrivateChannelInvitationRoleObserveButtonBackground: '#5a5a5a',

    BottomTabActive: '#FFFFFF',
    BottomTabInactive: '#5a5a5a',
    DisabledButton: '#5a5a5a',

    MapFill: 'rgba(222, 232, 244, 0.5)',
    MapBorder: 'rgba(45, 142, 156, 1)',
    DarkInput: '#1c1c1c',
    WeatherChartBackground: '#1c1c1c',

    TransparentButtonBorder: BaseColors.Black,
    TransparentButtonText: BaseColors.Black,
    TransparentButtonTextHover: BaseColors.White,
    LinkTextDark: '#b08f22',
    LinkTextLight: '#b08f22',
    ChartAxisLineStroke: '#5A5A5A',
    ChartAxisLabel: '#EAEAEA',
    UnderlineTabsActiveColor: '#b08f22',
    PrimaryIconBoxButtonTint: BaseColors.Black,
    ThemeOnWhite: '#b08f22',
    AddAttachmentButton: '#2D2D2D',
    AddAttachmentButtonContent: BaseColors.White,
    CheckboxPrimary: '#F8BA00',
    CheckboxTickLight: BaseColors.Black,
    CheckboxTickDark: BaseColors.Black,
    CheckboxInactiveLight: '#B9B9B9',
    CheckboxInactiveDark: '#604a14',
    MobileFlightInfoNatureOfFlightBase: '#5A5A5A',

    ChecklistTaskRadio: '#FFDD4F',
    AirportContactButton: '#2D2D2D',

    WeatherIconNormal: '#767676',
    WeatherIconRain: '#639CFF',
    WeatherIconSnow: '#4C9D9D',
    WeatherIconRainSnow: '#7E6CAE',
    GraphInactiveColor: '#5A5A5A',
    GraphBackground: '#1C1C1C',

    RunwayHelp: '#767676',
    RunwayHelpFade: '#4B4135',
    RunwayLegendDeparture: '#005DE5',
    ReportingModalBottomBackground: '#F57E20',
    RunwayMetar: rgba('#EAEAEA', 0.73),
    Onboarding: '#D5D3D1',
    StandsAndGatesItem: '#767676',
    OnboardingGradient1: '#EAEEEF',
    OnboardingGradient2: '#D5D3D1',

    DiscountModuleAreaName: '#EAEAEA',
    DiscountItemBackgroundMobile: '#2D2D2D',

    RunwayActive: '#B9B9B9',
    RunwayInactive: '#5A5A5A',

    NotificationSecondary: '#B9B9B9',
    NotamOutline: '#767676',
    AdvancedFilterBorder: '#5A5A5A',
    KanbanColumn: '#1C1C1C',
    KanbanTransparentColumn: '#1b2535e6',
    KanbanTransparentColumnLight: '#1b253580',
    KanbanTaskCancelledLabel: '#8F8F8F',
    HeaderMoreBadge: '#FFF1B8',
    HeaderMoreBadgeActive: '#F8BA00',
}

// Swedavia palette. Structurally identical to AoimsColors (single accent on
// a dark background) so every key required by the design system stays
// defined. Only the primary accent and core backgrounds are themed for now —
// the rest of the keys inherit AOIMS values which are neutral greys that
// blend with #1f1f1f.
const SwedaviaColors: typeof AosColors = {
    ...AoimsColors,

    // Primary accent — sampled from apps/aos-mobile/assets/icons/logo/swedavia.png
    Primary: '#659800',
    PrimaryLight: '#7FB81A',
    PrimaryDark: '#4D7300',
    PrimaryPale: '#2D4400',

    // Surfaces — match the launch screen + login (#1f1f1f) used on both platforms.
    BackgroundPrimary: '#1f1f1f',
    BackgroundLight: '#2A2A2A',
    BackgroundDark: '#141414',
    BackgroundSection: '#2A2A2A',
    // Redesign panel chrome (Figma EventBackground) — theme-agnostic dark.
    BackgroundCanvas: '#111114',
    BackgroundSubSurface: '#18181B',
    BackgroundElevation: '#262629',
    BorderSubtle: '#2A2D32',
    BorderStrong: '#505051',
    ContentOnGraphics: '#2B2B2B',
    StatusSuccess: '#54CC49',
    StatusWarning: '#F8D41D',
    StatusCritical: '#E72400',
    StatusCaution: '#FF8B1F',
    StatusNeutral: '#8B9EB4',
    BackgroundSectionLight: '#3A3A3A',

    // Text on the green Primary button — white reads better than a darker
    // green at this saturation. AOIMS uses a darker shade of its yellow
    // because yellow needs dark text for contrast; green doesn't.
    TextButtonLabel: '#FFFFFF',
    TextTheme: '#FFFFFF',

    // Hover / pressed shades for the primary button.
    PrimaryButtonHoverBackground: '#7FB81A',
    PrimaryOutlinedButtonHoverBackground: '#7FB81A',

    // Links + active tab underline — pick up the brand green.
    LinkTextDark: '#7FB81A',
    LinkTextLight: '#7FB81A',
    UnderlineTabsActiveColor: '#659800',
    ThemeOnWhite: '#4D7300',

    ContactFormAddNewButtonColor: '#7FB81A',
    OutlineButtonBackground: '#659800',
    RadioPrimary: '#659800',
    ChecklistTaskRadio: '#659800',
    CheckboxPrimary: '#659800',
    HeaderMoreBadgeActive: '#659800',
}

export const ColorConfig: Record<string, typeof AosColors> = {
    AOS: AosColors,
    AOIMS: AoimsColors,
    SWEDAVIA: SwedaviaColors,
}

export const Color = {
    ...BaseColors,
    ...ColorConfig[appConfig.theme],
}

// The app chrome is a dark surface on every tenant since the redesign, so the OS
// status bar always needs light content. AOIMS used to render a light/yellow
// chrome, hence the old per-tenant map — on the dark header it put black glyphs
// on a near-black bar (AOS-831).
export const statusBarColor = 'light-content'

export type Color =
    | (typeof BaseColors)[keyof typeof BaseColors]
    | (typeof AosColors)[keyof typeof AosColors]
    | (typeof AoimsColors)[keyof typeof AoimsColors]
